import * as React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import IconButton from '@material-ui/core/IconButton';
import '../../style/UserStyleSheet.css';
import api from '../../store/api';
import { useState } from 'react';
import LinearProgress from '@material-ui/core/LinearProgress';
import { useHistory } from "react-router-dom";
import throttle from 'lodash/throttle';
import { MuiThemeProvider } from "@material-ui/core/styles";
import theme from "../../components/shared/theme";
import Navbar from '../shared/Navbar';
import DateFnsUtils from '@date-io/date-fns';
import Box from "@material-ui/core/Box";
import { format, addDays, isToday, isTomorrow, isYesterday } from 'date-fns';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { Typography } from "@material-ui/core";
import { red } from '@material-ui/core/colors';
import { useDispatch } from 'react-redux';
import { showNavbar } from '../../store/modules/appStateSlice';

interface Activity {
    activityId: string,
    tableName: string,
    event: string,
    recordId: string,
    columnName: string,
    oldValue: string,
    newValue: string,
    createdBy: string,
    createdOn: string,
    message: string,
    name: string,
    userName: string
};

const useStyles = makeStyles((theme) => ({
    root: {
        marginLeft: 40,
        marginRight: 40,
    },
    heading: {
        fontSize: 38,
        fontWeight: 900,
        padding: theme.spacing(5, 0, 5, 0),
    },
    margin: {
        margin: theme.spacing(1),
    },
    withoutLabel: {
        marginTop: theme.spacing(3),
    },
    textField: {
        width: '25ch',
        backgroundColor: '#ECECEC',
        height: 30
    },
    formBtn: {
        width: '100px',
    },
    gridStyle: {
        padding: theme.spacing(5, 0, 5, 0)
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
    loading: {
        width: '100%',
        '& > * + *': {
            marginTop: theme.spacing(2, 0, 0, 50),
        },
    },
    textFieldRoot: {
        width: '90%',
        height: 44,

    },
    textFieldInput: {
        backgroundColor: '#ECECEC',
        color: 'black',
        height: '44px',
        border: 'none',
        display: 'flex',
        flexDirection: 'row-reverse',
    },
    tableRow: {
        cursor: 'pointer'
    },
    dateFilterIcon: {
        color: "#0049a3",
        "&:hover": {
            backgroundColor: "#fff",
        }
    },
}));

const headCells = [
    { id: 'createdOn', numeric: false, disablePadding: true, label: 'Date/Time' },
    { id: 'message', numeric: false, disablePadding: false, label: 'Event Type' }
];

function ActivityViewPage(): JSX.Element {
    const classes = useStyles();
    const [activityList, setActivityList] = useState<Activity[]>([]);
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('createdOn');
    const [page, setPage] = React.useState(0);
    const [recordCount, setRecordCount] = React.useState(0);
    const [dense, setDense] = React.useState(false);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [searchText, setSearchText] = React.useState('');
    const [loading, setLoading] = useState(false);
    const history = useHistory();
    const [selectedDay, setSelectedDay] = React.useState(format(new Date(), 'yyyy-MM-dd'));
    const dispatch = useDispatch();

    React.useEffect(() => {
        getAllActivites(0, '', 'desc', 'createdOn', selectedDay);
    }, [selectedDay]);

    React.useEffect(() => {
        dispatch(showNavbar(false));
    }, [])


    //Get All Activities
    const getAllActivites = React.useMemo(() => throttle(async (updatePageNumber: any, keyword: string, sortOrder: any, sortColumn: any, selectedDate: any): Promise<void> => {
        setActivityList([]);
        setRecordCount(0);
        if (updatePageNumber === 0)
            setPage(0);

        setLoading(true);
        const _res = await api.getActivitiesByPage('activityDate=' + selectedDate + '&pageNumber=' + (updatePageNumber ? updatePageNumber : page) + '&pageSize=10&sortColumn=' + sortColumn + '&sortdirection=' + sortOrder);
        if (_res !== null && _res.data !== null && _res.data.model !== null) {
            
            if (_res.data.itemsCount > 0) {
                setRecordCount(_res.data.itemsCount);
                setActivityList(_res.data.model);           
            }
            else {
                setActivityList([]);
            }
        }
        else {
            setActivityList([]);
        }
        setLoading(false);
    }, 200), []);

    const createSortHandler = (event: any, column: any) => {
        let order_str = (orderBy == column ? (order === 'asc' ? 'desc' : 'asc') : 'asc');
        setOrder(order_str);
        setOrderBy(column);
        getAllActivites(page, searchText, order_str, column, selectedDay);
    };

    const handleChangePage = (event: any, newPage: any) => {
        const updatePage = newPage + 1;
        setPage(newPage);
        getAllActivites(updatePage, searchText, order, orderBy, selectedDay);
    };

    const CreateLoadingLine = (props: any): JSX.Element => {
        return (
            <div className={classes.loading}>
                <LinearProgress color="primary" />
            </div>
        )
    }

    const getPrettyDay = (selectedDay: string) => {
        if (isToday(new Date(selectedDay))) {
            return "Today";
        } else if (isTomorrow(new Date(selectedDay))) {
            return "Tomorrow";
        } else if (isYesterday(new Date(selectedDay))) {
            return "Yesterday";
        } else {
            return format(new Date(selectedDay), "dd/MM/yy");
        }
    }

    const renderTag = (text: string) => {
        let _args = text.split('.');
        return <div>{_args[0] ?? ''} <span>{_args[1] ?? ''}</span> {_args[2] ?? ''} <span >{_args[3] ?? ''}</span></div>;
    }

    return (
        <React.Fragment >
            <MuiThemeProvider theme={theme} >
                <Navbar />

                <div style={{ marginLeft: 'auto', marginRight: 0, paddingTop: 15 }}>
                    <div style={loading ? {pointerEvents: "none", opacity: "0.4"} : {}}> 
                        <Box display="flex" alignItems="center" component="span"  >
                            <IconButton className={classes.dateFilterIcon} onClick={() => setSelectedDay((prev) => format(addDays(new Date(selectedDay), -1), "yyyy-MM-dd"))}>
                                <ArrowBackIosIcon style={{ fontSize:20}} />
                            </IconButton>
                            <Box mx={3} ><Typography className={classes.dateFilterIcon} variant="subtitle1">{getPrettyDay(selectedDay)}</Typography></Box>
                            <IconButton className={classes.dateFilterIcon} onClick={() => setSelectedDay((prev) => format(addDays(new Date(selectedDay), 1), "yyyy-MM-dd"))}>
                                <ArrowForwardIosIcon style={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'stretch', flexGrow: 1, }}>
                    <div style={{ width: '100%', }}>
                        <div style={loading ? {pointerEvents: "none", opacity: "0.4"} : {}}>
                            <TableContainer>
                                <Table
                                    aria-labelledby="tableTitle"
                                    size={dense ? 'small' : 'medium'}
                                    aria-label="enhanced table">

                                    <TableHead>
                                        <TableRow>
                                            {headCells.map((headCell) => (
                                                <TableCell
                                                    style={{ width: 20 }}
                                                    key={headCell.id}
                                                    align={headCell.numeric ? 'right' : 'left'}
                                                    sortDirection={orderBy === headCell.id ? 'asc' : false}>
                                                    <TableSortLabel
                                                        active={orderBy === headCell.id}
                                                        direction={orderBy === headCell.id ? (order === 'asc' ? 'asc' : 'desc') : undefined}
                                                        onClick={(e => createSortHandler(e, headCell.id))}>
                                                        {headCell.label}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {setActivityList.length > 0 ?
                                            activityList.map((row: Activity) => {
                                                return (
                                                    <TableRow
                                                        className={classes.tableRow}
                                                        //onClick={(event) => editUserClick(event, row.userId)}
                                                        role="checkbox"
                                                        tabIndex={-1}
                                                        key={row.createdOn}>
                                                        <TableCell component="th" scope="row">
                                                            {format(new Date(row.createdOn), "yyyy-MM-dd HH:mm:ss")}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {renderTag(row.message)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                            :
                                            <TableRow>
                                                <TableCell align="center" colSpan={5}>No data available</TableCell>
                                            </TableRow>
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[10]}
                                component="div"
                                count={recordCount}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onChangePage={handleChangePage}
                            />
                        </div>

                        <div style={{ marginTop: 5, marginBottom: 20, }}>
                            {loading ? <CreateLoadingLine /> : null}
                        </div>
                    </div>

                </div>

            </MuiThemeProvider>
        </React.Fragment>
    )
}

export default React.memo(ActivityViewPage);